import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import * as authService from '@/services/authService';
import type { User, Location } from '@/services/authService';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3003/api';

export interface AuthState {
  user: User | null;
  selectedLocation: Location | null;
  locations: Location[];
  isLoading: boolean;
  error: string | null;
  isAuthenticated: boolean;
}

export interface AuthActions {
  login: (username: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  selectLocation: (location: Location | null) => void;
}

export type AuthContextType = AuthState & AuthActions;

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    user: null,
    selectedLocation: null,
    locations: [],
    isLoading: true, // Cambiar a true inicialmente
    error: null,
    isAuthenticated: false,
  });

  // Restaurar estado de autenticación al cargar
  useEffect(() => {
    const restoreAuthState = () => {
      try {
        const token = localStorage.getItem('accessToken'); // Usar la clave estándar
        const userStr = localStorage.getItem('macartur:user');
        const lastLocationStr = localStorage.getItem('macartur:lastSelectedLocation');

        if (token && userStr) {
          const user = JSON.parse(userStr);
          const lastLocation = lastLocationStr ? JSON.parse(lastLocationStr) : null;
          
          // Cargar ubicaciones accesibles para el usuario
          const loadLocations = async () => {
            try {
              // Usar locationsDetails del usuario si está disponible (viene del login)
              // Esto evita hacer llamadas adicionales al API
              let locations = [];

              if (user.locationsDetails && Array.isArray(user.locationsDetails) && user.locationsDetails.length > 0) {
                // Usar los detalles que ya vienen del login
                locations = user.locationsDetails.map((loc: any) => ({
                  id: loc.id.toString(),
                  name: loc.name,
                  companyId: loc.companyId || user.companyId,
                  description: loc.address || '',
                  address: loc.address || '',
                }));
                console.log('✅ Using locationsDetails from user data - no extra API calls');
              } else {
                // Fallback: obtener ubicaciones desde la API (una sola llamada)
                console.log('📍 No locationsDetails found, fetching from API...');
                locations = await authService.getCompanyLocations(user.companyId, token);
              }

              setState(prev => ({
                ...prev,
                user: user,
                locations: locations,
                selectedLocation: lastLocation,
                isAuthenticated: true,
                isLoading: false,
              }));

              console.log('✅ Auth state restored with locations for user:', user.nombre, '- Locations:', locations.length);
            } catch (error) {
              console.warn('⚠️ Error loading locations:', error);

              setState(prev => ({
                ...prev,
                user: user,
                locations: [],
                selectedLocation: lastLocation || null,
                isAuthenticated: true,
                isLoading: false,
              }));

              console.log('⚠️ Auth state restored without locations for user:', user.nombre);
            }
          };
          
          // Cargar ubicaciones desde employee_location_access
          loadLocations();
        } else {
          setState(prev => ({
            ...prev,
            isLoading: false,
          }));
        }
      } catch (error) {
        console.error('❌ Error restoring auth state:', error);
        // En caso de error, limpiar storage corrupto
        localStorage.removeItem('accessToken');
        localStorage.removeItem('macartur:authToken');
        localStorage.removeItem('macartur:user');
        localStorage.removeItem('macartur:lastSelectedLocation');
        
        setState(prev => ({
          ...prev,
          isLoading: false,
        }));
      }
    };

    restoreAuthState();
  }, []);

  // Debug: Log cada cambio de estado
  useEffect(() => {
    console.log('🔄 AuthContext state changed:', {
      isAuthenticated: state.isAuthenticated,
      hasUser: !!state.user,
      hasSelectedLocation: !!state.selectedLocation,
      locationsCount: state.locations.length,
      isLoading: state.isLoading,
      error: state.error
    });
  }, [state]);

  const login = async (username: string, password: string): Promise<void> => {
    // Limpiar ubicación guardada para empezar fresh
    localStorage.removeItem('macartur:lastSelectedLocation');
    
    setState(prev => ({
      ...prev,
      isLoading: true,
      error: null,
    }));

    try {
      const response = await authService.login(username, password);
      
      // Guardar token
      localStorage.setItem('accessToken', response.token);
      localStorage.setItem('macartur:user', JSON.stringify(response.user));

      // Si es CAJERO con más de 1 local, NO auto-seleccionar — dejar que elija en LoginPage
      // Para otros roles, auto-seleccionar priorizando local PRINCIPAL
      let userLocation: Location | null = null;
      const isCajero = response.user.rol === 'CAJERO';
      if (isCajero && response.locations.length > 1) {
        userLocation = null; // Forzar selección manual
      } else {
        const principalLocation = response.locations.find((loc: any) => loc.tipoLocal === 'PRINCIPAL');
        userLocation = principalLocation || response.locations[0] || null;
      }

      // Guardar la ubicación seleccionada (solo si se auto-seleccionó)
      if (userLocation) {
        localStorage.setItem('macartur:lastSelectedLocation', JSON.stringify(userLocation));
      }

      setState(prev => ({
        ...prev,
        user: response.user,
        locations: response.locations,
        selectedLocation: userLocation,
        isAuthenticated: true,
        isLoading: false,
        error: null,
      }));
      
      console.log('✅ Login successful:', response.user.nombre, response.user.apellidos, '- Selected location:', userLocation?.name || 'None');
    } catch (error) {
      setState(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Error de autenticación',
      }));
    }
  };

  const logout = async (): Promise<void> => {
    try {
      await authService.logout();
      
      // Limpiar también la ubicación guardada y tokens
      localStorage.removeItem('accessToken');
      localStorage.removeItem('macartur:authToken');
      localStorage.removeItem('macartur:lastSelectedLocation');
      
      setState({
        user: null,
        selectedLocation: null,
        locations: [],
        isLoading: false,
        error: null,
        isAuthenticated: false,
      });
    } catch (error) {
      // En caso de error en logout, limpiar estado local de todos modos
      localStorage.removeItem('accessToken');
      localStorage.removeItem('macartur:authToken');
      localStorage.removeItem('macartur:lastSelectedLocation');
      
      setState({
        user: null,
        selectedLocation: null,
        locations: [],
        isLoading: false,
        error: null,
        isAuthenticated: false,
      });
    }
  };

  const selectLocation = (location: Location | null): void => {
    console.log('📍 Selecting location:', location?.name || 'null');
    
    setState(prev => {
      console.log('📍 Previous state before location selection:', {
        isAuthenticated: prev.isAuthenticated,
        hasUser: !!prev.user,
        hasSelectedLocation: !!prev.selectedLocation,
        locationsCount: prev.locations.length
      });
      
      return {
        ...prev,
        selectedLocation: location,
      };
    });

    if (location) {
      localStorage.setItem('macartur:lastSelectedLocation', JSON.stringify(location));
    } else {
      localStorage.removeItem('macartur:lastSelectedLocation');
    }
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    selectLocation,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};